import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PortalCashTransactionDetail, type CashTransactionDetail } from '@kioskgaming/ui';
import AppLayout from '../../components/layout/AppLayout';
import { getShopCashTransactionDetail } from '../../services/api';

export default function CounterDepositDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [transaction, setTransaction] = useState<CashTransactionDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    const createdAt = searchParams.get('created_at') ?? searchParams.get('createdAt') ?? undefined;
    if (!createdAt) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void getShopCashTransactionDetail(id, createdAt)
      .then((res) => {
        if (!res.success) throw new Error(res.message || 'Counter deposit not found');
        const d = (res as { data?: Record<string, unknown> }).data;
        if (!d?.transaction) {
          setTransaction(null);
          return;
        }
        const t = d.transaction as Record<string, unknown>;
        setTransaction({
          ...(t as CashTransactionDetail),
          performedByType: String(t.performedByType ?? t.performed_by_type ?? 'shop'),
          performedById: String(t.performedById ?? t.performed_by_id ?? ''),
          buyerBalanceAfter: Number(t.buyerBalanceAfter ?? t.buyer_balance_after ?? 0),
          updatedAt: String(t.updatedAt ?? t.updated_at ?? ''),
          events: ((d.events as unknown[]) ?? []).map((ev) => {
            const e = ev as Record<string, unknown>;
            return {
              id: String(e.id),
              eventType: String(e.eventType ?? e.event_type ?? 'created'),
              actorType: String(e.actorType ?? e.actor_type ?? 'shop'),
              actorId: (e.actorId ?? e.actor_id) as string | null | undefined,
              payload: (e.payload as Record<string, unknown>) ?? {},
              createdAt: String(e.createdAt ?? e.created_at ?? ''),
            };
          }),
          creditTransaction: d.creditTransaction
            ? {
                id: String((d.creditTransaction as Record<string, unknown>).id),
                amount: Number((d.creditTransaction as Record<string, unknown>).amount ?? 0),
                balanceAfter: Number(
                  (d.creditTransaction as Record<string, unknown>).balanceAfter ??
                    (d.creditTransaction as Record<string, unknown>).balance_after ??
                    0,
                ),
                description:
                  ((d.creditTransaction as Record<string, unknown>).description as string) ?? null,
                createdAt: String(
                  (d.creditTransaction as Record<string, unknown>).createdAt ??
                    (d.creditTransaction as Record<string, unknown>).created_at ??
                    '',
                ),
              }
            : null,
          performedBy: d.performedBy
            ? {
                id: String((d.performedBy as Record<string, unknown>).id),
                email: ((d.performedBy as Record<string, unknown>).email as string) ?? null,
                name: ((d.performedBy as Record<string, unknown>).name as string) ?? null,
              }
            : null,
        });
      })
      .catch((e: unknown) => {
        setTransaction(null);
        toast.error(e instanceof Error ? e.message : 'Could not load counter deposit');
      })
      .finally(() => setLoading(false));
  }, [id, searchParams]);

  return (
    <AppLayout>
      <div className="mb-4">
        <Link
          to="/cash-settlement/deposits"
          className="text-sm font-medium text-emerald-700 hover:underline"
        >
          ← Back to counter deposits
        </Link>
      </div>
      <div className="rounded-xl border border-stone-200 bg-white shadow-sm">
        <PortalCashTransactionDetail loading={loading} transaction={transaction} />
      </div>
    </AppLayout>
  );
}
